import React, { useState, useCallback, memo, useRef, useEffect } from "react";
import {
  Box,
  Container,
  Flex,
  Heading,
  Text,
  Grid,
  GridItem,
  VStack,
  HStack,
  Icon,
  useColorModeValue,
  Badge,
  Button,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useDisclosure,
  ButtonGroup,
  Circle,
} from "@chakra-ui/react";
import { 
  FaChartBar, 
  FaChartPie, 
  FaChartLine, 
  FaMoneyBillWave,
  FaArrowRight,
  FaTrophy,
  FaRocket,
  FaPercentage,
  FaTags,
  FaCheck,
  FaTimes,
  FaExclamation,
  FaRobot,
} from "react-icons/fa";
import { ChevronDownIcon } from "@chakra-ui/icons";
import { MdEqualizer, MdRocketLaunch } from "react-icons/md";
import ApexCharts from "react-apexcharts";
import { motion } from "framer-motion";
import WaitlistModal from "./WaitlistModal";

// Inserting win-loss data definitions after the last import
// Context: after the import of Card

// Re-adding win-loss analysis data used in this file
const leadSourceData = {
  sources: ['Referral', 'Website', 'Direct', 'Partner', 'Social'],
  won: [32, 18, 14, 21, 15],
  lost: [8, 12, 10, 15, 6],
};

const teamMemberData = {
  members: ['Jordan Belfort', 'Donnie Azoff', 'Mark Hanna', 'Nicky Koskoff', 'Brad Bodnick'],
  won: [25, 18, 22, 15, 20],
  lost: [5, 8, 7, 12, 9],
};

// Section component that alternates layout
const DashboardSection = memo(({ 
  title, 
  description, 
  chartComponent, 
  icon, 
  iconColor, 
  isReversed = false,
  bgColor,
  headerComponent = null,
  showButton = false
}) => {
  const borderColor = useColorModeValue("gray.200", "gray.700");

  return (
    <Box
      py={{ base: 8, md: 12 }} 
      bg={bgColor}
    >
      <Container maxW="1400px" px={{ base: 4, md: 8 }}>
        <Grid 
          templateColumns={{ base: "1fr", lg: "1fr 1fr" }} 
          gap={{ base: 6, lg: 8 }}
          alignItems="center"
          direction={isReversed ? "row-reverse" : "row"}
        >
          {/* Text Section */}
          <GridItem order={{ base: 1, lg: isReversed ? 2 : 1 }}>
            <Box maxW="600px" mx={isReversed ? { lg: "auto" } : "0"}>
              <Flex
                bg={`${iconColor}.50`}
                borderRadius="full"
                p={2}
                alignItems="center"
                justifyContent="center"
                boxSize={{ base: "50px", md: "60px" }}
                mb={3}
              >
                <Icon as={icon} color={`${iconColor}.500`} boxSize={{ base: 6, md: 8 }} />
              </Flex>
      
              <Heading 
                as="h2" 
                fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
                fontWeight="bold"
                mb={2}
              >
                {title}
              </Heading>
              
              <Text 
                fontSize={{ base: "md", md: "lg" }}
                color={useColorModeValue("gray.600", "gray.400")}
                mb={4}
              >
                {description}
              </Text>
              
              {showButton && (
              <Button 
                  size="md" 
                colorScheme={iconColor} 
                rightIcon={<Icon as={FaArrowRight} />}
              >
                View Reports
              </Button>
              )}
            </Box>
          </GridItem>
          
          {/* Chart Section */}
          <GridItem order={{ base: 2, lg: isReversed ? 1 : 2 }}>
            <Box 
              borderRadius="lg" 
          borderWidth="1px" 
          borderColor={borderColor}
              overflow="hidden"
              boxShadow="md"
              bg={useColorModeValue("white", "gray.800")}
          p={4}
              height="100%"
              minH="320px"
            >
              {headerComponent && (
                <Box mb={4} borderBottomWidth="1px" borderColor={borderColor} pb={3}>
                  {headerComponent}
                </Box>
              )}
              {chartComponent}
              </Box>
          </GridItem>
      </Grid>
      </Container>
    </Box>
  );
});

// Add display name
DashboardSection.displayName = "DashboardSection";

// Mini Stat Component
const MiniStat = memo(({ label, value, change, isIncrease, icon, iconBg }) => {
  return (
    <Stat
      px={{ base: 2, md: 3 }}
      py={{ base: 2, md: 3 }}
      bg={useColorModeValue("white", "gray.700")}
      shadow="sm"
      border="1px solid"
      borderColor={useColorModeValue("gray.200", "gray.500")}
      rounded="lg"
    >
      <Flex justifyContent="space-between">
        <Box pl={{ base: 0, md: 2 }}>
          <StatLabel fontWeight="medium" fontSize="sm" isTruncated>
            {label}
          </StatLabel>
          <StatNumber fontSize={{ base: "lg", md: "xl" }} fontWeight="bold">
            {value}
          </StatNumber>
          {change && (
            <StatHelpText mb={0}>
              <StatArrow type={isIncrease ? "increase" : "decrease"} />
              {change}
            </StatHelpText>
          )}
        </Box>
        <Box
          my="auto"
          color={useColorModeValue("gray.800", "gray.200")}
          alignContent="center"
        >
          <Circle size={{ base: "36px", md: "40px" }} bg={iconBg} color="white">
            <Icon as={icon} boxSize={{ base: 4, md: 5 }} />
          </Circle>
        </Box>
      </Flex>
    </Stat>
  );
});

// Add display name
MiniStat.displayName = "MiniStat";

// Now create independent components for each chart section
// Performance section with its own state
const PerformanceSection = memo(({ bgColor, iconColor = "orange" }) => {
  const [selectedYear, setSelectedYear] = useState("2023");
  const [selectedQuarter, setSelectedQuarter] = useState("Q3");
  
  // Define different data sets for different years and quarters
  const performanceData = {
    "2023": {
      "Q1": {
        revenue: "$520K",
        revenueChange: "18%",
        deals: "28",
        dealsChange: "12%",
        winRate: "61%",
        winRateChange: "3%",
        avgDealSize: "$18.6K",
        avgDealSizeChange: "5%"
      },
      "Q2": {
        revenue: "$685K",
        revenueChange: "15%",
        deals: "35",
        dealsChange: "10%",
        winRate: "65%",
        winRateChange: "4%",
        avgDealSize: "$19.5K",
        avgDealSizeChange: "6%"
      },
      "Q3": {
        revenue: "$837K",
        revenueChange: "23%",
        deals: "42",
        dealsChange: "15%",
        winRate: "68%",
        winRateChange: "5%",
        avgDealSize: "$19.9K",
        avgDealSizeChange: "7%"
      },
      "Q4": {
        revenue: "$942K",
        revenueChange: "26%",
        deals: "46",
        dealsChange: "18%",
        winRate: "72%",
        winRateChange: "8%",
        avgDealSize: "$20.5K",
        avgDealSizeChange: "9%"
      },
      "Full Year": {
        revenue: "$2.98M",
        revenueChange: "21%",
        deals: "151",
        dealsChange: "14%",
        winRate: "67%",
        winRateChange: "5%",
        avgDealSize: "$19.7K",
        avgDealSizeChange: "7%"
      }
    },
    "2022": {
      "Q1": {
        revenue: "$412K",
        revenueChange: "12%",
        deals: "23",
        dealsChange: "8%",
        winRate: "58%",
        winRateChange: "2%",
        avgDealSize: "$17.9K",
        avgDealSizeChange: "4%"
      },
      "Q2": {
        revenue: "$563K",
        revenueChange: "11%",
        deals: "30",
        dealsChange: "7%",
        winRate: "60%",
        winRateChange: "3%",
        avgDealSize: "$18.8K",
        avgDealSizeChange: "5%"
      },
      "Q3": {
        revenue: "$683K",
        revenueChange: "16%",
        deals: "37",
        dealsChange: "9%",
        winRate: "63%",
        winRateChange: "4%",
        avgDealSize: "$18.5K",
        avgDealSizeChange: "3%"
      },
      "Q4": {
        revenue: "$775K",
        revenueChange: "19%",
        deals: "40",
        dealsChange: "12%",
        winRate: "65%",
        winRateChange: "5%",
        avgDealSize: "$19.4K",
        avgDealSizeChange: "6%"
      },
      "Full Year": {
        revenue: "$2.43M",
        revenueChange: "15%",
        deals: "130",
        dealsChange: "9%",
        winRate: "61%",
        winRateChange: "3%",
        avgDealSize: "$18.7K",
        avgDealSizeChange: "4%"
      }
    },
    "2021": {
      "Q1": {
        revenue: "$358K",
        revenueChange: "8%",
        deals: "20",
        dealsChange: "5%",
        winRate: "56%",
        winRateChange: "1%",
        avgDealSize: "$17.9K",
        avgDealSizeChange: "2%"
      },
      "Q2": {
        revenue: "$485K",
        revenueChange: "9%",
        deals: "27",
        dealsChange: "6%",
        winRate: "57%",
        winRateChange: "2%",
        avgDealSize: "$18.0K",
        avgDealSizeChange: "3%"
      },
      "Q3": {
        revenue: "$589K",
        revenueChange: "11%",
        deals: "33",
        dealsChange: "7%",
        winRate: "59%",
        winRateChange: "2%",
        avgDealSize: "$17.8K",
        avgDealSizeChange: "2%"
      },
      "Q4": {
        revenue: "$652K",
        revenueChange: "13%",
        deals: "35",
        dealsChange: "8%",
        winRate: "60%",
        winRateChange: "3%",
        avgDealSize: "$18.6K",
        avgDealSizeChange: "4%"
      },
      "Full Year": {
        revenue: "$2.08M",
        revenueChange: "10%",
        deals: "115",
        dealsChange: "6%",
        winRate: "58%",
        winRateChange: "2%",
        avgDealSize: "$18.1K",
        avgDealSizeChange: "3%"
      }
    }
  };
  
  // Page Header Style Dropdowns for Sales Performance
  const PerformanceHeaderControls = useCallback(() => {
    const buttonBg = useColorModeValue("white", "gray.600");
    const buttonBorderColor = useColorModeValue("gray.300", "gray.500");
    const buttonHoverBg = useColorModeValue("gray.50", "gray.500");

  return (
      <Flex justify="flex-start" align="center">
        <HStack spacing={3}>
          <Menu>
            <MenuButton
              as={Button}
              rightIcon={<ChevronDownIcon />}
              bg={buttonBg}
              borderColor={buttonBorderColor}
              borderWidth="1px"
              _hover={{ bg: buttonHoverBg }}
              _active={{ bg: buttonHoverBg }}
              size="sm"
            >
              {selectedYear}
            </MenuButton>
            <MenuList>
              <MenuItem onClick={() => {
                setSelectedYear("2023");
              }}>2023</MenuItem>
              <MenuItem onClick={() => {
                setSelectedYear("2022");
              }}>2022</MenuItem>
              <MenuItem onClick={() => {
                setSelectedYear("2021");
              }}>2021</MenuItem>
            </MenuList>
          </Menu>
          <Menu>
            <MenuButton
              as={Button}
              rightIcon={<ChevronDownIcon />}
              bg={buttonBg}
              borderColor={buttonBorderColor}
              borderWidth="1px"
              _hover={{ bg: buttonHoverBg }}
              _active={{ bg: buttonHoverBg }}
              size="sm"
            >
              {selectedQuarter}
            </MenuButton>
            <MenuList>
              <MenuItem onClick={() => {
                setSelectedQuarter("Full Year");
              }}>Full Year</MenuItem>
              <MenuItem onClick={() => {
                setSelectedQuarter("Q1");
              }}>Q1</MenuItem>
              <MenuItem onClick={() => {
                setSelectedQuarter("Q2");
              }}>Q2</MenuItem>
              <MenuItem onClick={() => {
                setSelectedQuarter("Q3");
              }}>Q3</MenuItem>
              <MenuItem onClick={() => {
                setSelectedQuarter("Q4");
              }}>Q4</MenuItem>
            </MenuList>
          </Menu>
        </HStack>
      </Flex>
    );
  }, [selectedYear, selectedQuarter]);
  
  // MiniStats for Sales Performance
  const PerformanceStats = useCallback(() => {
    // Get the data for the selected year and quarter
    const data = performanceData[selectedYear]?.[selectedQuarter] || {
      revenue: "N/A",
      revenueChange: "0%",
      deals: "N/A",
      dealsChange: "0%",
      winRate: "N/A",
      winRateChange: "0%",
      avgDealSize: "N/A",
      avgDealSizeChange: "0%"
    };
    
    return (
      <SimpleGrid columns={2} spacing={{ base: 2, md: 3 }} mt={3}>
        <MiniStat 
          label="Revenue" 
          value={data.revenue} 
          change={data.revenueChange} 
          isIncrease={true} 
          icon={FaMoneyBillWave} 
          iconBg="orange.500"
        />
        <MiniStat 
          label="Deals Won" 
          value={data.deals} 
          change={data.dealsChange} 
          isIncrease={true} 
          icon={FaTrophy} 
          iconBg="orange.500"
        />
        <MiniStat 
          label="Win Rate" 
          value={data.winRate} 
          change={data.winRateChange} 
          isIncrease={true} 
          icon={FaPercentage} 
          iconBg="orange.500"
        />
        <MiniStat 
          label="Avg Deal Size" 
          value={data.avgDealSize} 
          change={data.avgDealSizeChange} 
          isIncrease={true} 
          icon={FaTags} 
          iconBg="orange.500"
        />
      </SimpleGrid>
    );
  }, [selectedYear, selectedQuarter, performanceData]);
  
  // Complete Performance Section Content (just header and stats now)
  const PerformanceSectionContent = useCallback(() => {
    return (
      <Box>
        <PerformanceHeaderControls />
        <PerformanceStats />
      </Box>
    );
  }, [selectedYear, selectedQuarter, PerformanceHeaderControls, PerformanceStats]);

  return (
    <DashboardSection
      title="Sales Performance"
      description="Track key metrics at a glance. View revenue trends, deal counts, and win rates in one place to identify opportunities and allocate resources effectively."
      icon={FaChartBar}
      iconColor={iconColor}
      bgColor={bgColor}
      isReversed={false}
      chartComponent={<PerformanceSectionContent />}
      showButton={false}
    />
  );
});

// Add display name
PerformanceSection.displayName = "PerformanceSection";

// Target Sales section with its own state
const TargetSalesSection = memo(({ bgColor, iconColor = "green" }) => {
  const [targetPreset, setTargetPreset] = useState("standard");
  
  // Sample closed won data
  const closedWonData = [65, 75, 110, 85, 90, 140, 120, 130, 95, 105, 120, 130];
  const projectedData = [65, 75, 110, 85, 90, 140, 125, 135, 115, 125, 140, 155];
  
  // Calculate targets based on the selected preset
  const getTargetsByPreset = useCallback((preset, baseTargets) => {
    if (preset === "conservative") {
      return baseTargets.map(target => Math.round(target * 0.8));
    } else if (preset === "aggressive") {
      return baseTargets.map(target => Math.round(target * 1.2));
    }
    
    // Standard targets (default)
    return baseTargets;
  }, []);

  // Sales Target Button Group Options
  const TargetOptions = useCallback(() => {
    const inactiveTextColor = useColorModeValue("gray.700", "gray.200");
    
    return (
      <Flex justify="flex-start" align="center">
        <ButtonGroup size="sm" spacing={0} borderRadius="md" borderWidth="1px" borderColor="gray.200">
          <Button 
            colorScheme="blue" 
            variant={targetPreset === "conservative" ? "solid" : "ghost"}
            borderRightWidth="1px"
            borderRightColor="gray.200"
            borderRadius="0"
            _first={{ borderLeftRadius: "md" }}
            onClick={() => {
              setTargetPreset("conservative");
            }}
            color={targetPreset === "conservative" ? "white" : inactiveTextColor}
          >
            Conservative
          </Button>
          <Button 
            colorScheme="blue" 
            variant={targetPreset === "standard" ? "solid" : "ghost"}
            borderRightWidth="1px"
            borderRightColor="gray.200"
            borderRadius="0"
            onClick={() => {
              setTargetPreset("standard");
            }}
            color={targetPreset === "standard" ? "white" : inactiveTextColor}
          >
            Standard
          </Button>
          <Button 
            colorScheme="blue" 
            variant={targetPreset === "aggressive" ? "solid" : "ghost"}
            borderRadius="0"
            _last={{ borderRightRadius: "md" }}
            onClick={() => {
              setTargetPreset("aggressive");
            }}
            color={targetPreset === "aggressive" ? "white" : inactiveTextColor}
          >
            Aggressive
          </Button>
        </ButtonGroup>
      </Flex>
    );
  }, [targetPreset]);
  
  // Custom Line Chart for Target Sales with percentage labels
  const TargetSalesChart = useCallback(() => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const baseTargets = [70, 80, 100, 90, 110, 130, 140, 140, 120, 110, 130, 150];
    const targetData = getTargetsByPreset(targetPreset, baseTargets);
    
    // Recalculate percentages based on the selected preset
    const presetPercentages = closedWonData.map((value, index) => {
      const target = targetData[index];
      if (!target) return '0%';
      return `${Math.round((value / target) * 100)}%`;
    });
    
    const options = {
      chart: {
        type: 'line',
        toolbar: {
          show: false
        },
        zoom: {
          enabled: false
        }
      },
      colors: ['#1A5BF6', '#34D399', '#94A3B8'],
      markers: {
        size: 4
      },
      stroke: {
        curve: 'smooth',
        width: [3, 3, 2]
      },
      xaxis: {
        categories: months
      },
      yaxis: {
        title: {
          text: 'Amount ($K)'
        },
        labels: {
          formatter: (value) => {
            return value.toFixed(0);
          }
        }
      },
      dataLabels: {
        enabled: true,
        enabledOnSeries: [0],
        formatter: function(val, opts) {
          const dataPointIndex = opts.dataPointIndex;
          return presetPercentages[dataPointIndex];
        },
        offsetY: -10,
        style: {
          fontSize: '12px',
          colors: ['#1A5BF6']
        },
        background: {
          enabled: true,
          foreColor: '#fff',
          borderRadius: 4,
          padding: 4,
          opacity: 0.9,
          borderWidth: 1,
          borderColor: '#1A5BF6'
        }
      },
      tooltip: {
        enabled: true,
        shared: true,
        intersect: false,
        y: {
          formatter: function(y) {
            if (typeof y !== "undefined") {
              return `$${y}K`;
            }
            return y;
          }
        }
      },
      legend: {
        position: 'top',
        horizontalAlign: 'right'
      }
    };
    
    const series = [
      {
        name: "Closed Won",
        data: closedWonData
      },
      {
        name: "Target",
        data: targetData
      },
      {
        name: "Projected",
        data: projectedData
      }
    ];
    
    return (
      <Box>
        <TargetOptions />
        <Box mt={4} height="280px">
          <ApexCharts 
            options={options} 
            series={series} 
            type="line" 
            height="100%" 
          />
        </Box>
      </Box>
    );
  }, [targetPreset, getTargetsByPreset, TargetOptions]);

  return (
    <DashboardSection
      title="Sales to Target"
      description="Monitor progress in real-time. Set goals, track achievements, and keep your team aligned with intuitive visualizations that highlight the path to success."
      icon={FaTrophy}
      iconColor={iconColor}
      bgColor={bgColor}
      isReversed={true}
      chartComponent={<TargetSalesChart />}
      showButton={false}
    />
  );
});

// Add display name
TargetSalesSection.displayName = "TargetSalesSection";

// Win-Loss Analysis section with its own state
const WinLossSection = memo(({ bgColor, iconColor = "blue" }) => {
  const [analysisView, setAnalysisView] = useState("source");
  
  // Win-Loss Analysis Toggle
  const WinLossToggle = useCallback(() => {
    // Colors for button group
    const inactiveTextColor = useColorModeValue("gray.700", "gray.200");
    
    return (
      <Flex 
        justify={{ base: "center", sm: "space-between" }} 
        align="center" 
        mb={4}
        flexDirection={{ base: "column", sm: "row" }}
        gap={{ base: 3, sm: 0 }}
      >
        <ButtonGroup size="sm" spacing={0} borderRadius="md" borderWidth="1px" borderColor="gray.200">
          <Button 
            colorScheme="blue" 
            variant={analysisView === "source" ? "solid" : "ghost"}
            borderRightWidth="1px"
            borderRightColor="gray.200"
            borderRadius="0"
            _first={{ borderLeftRadius: "md" }}
            onClick={() => {
              setAnalysisView("source");
            }}
            color={analysisView === "source" ? "white" : inactiveTextColor}
          >
            By Lead Source
          </Button>
          <Button 
            colorScheme="blue" 
            variant={analysisView === "team" ? "solid" : "ghost"}
            borderRadius="0"
            _last={{ borderRightRadius: "md" }}
            onClick={() => {
              setAnalysisView("team");
            }}
            color={analysisView === "team" ? "white" : inactiveTextColor}
          >
            By Team Member
          </Button>
        </ButtonGroup>
        
        {/* Win-Loss Stats Summary */}
        <HStack spacing={{ base: 2, md: 4 }} justify="center" wrap="wrap">
          <HStack>
            <Icon as={FaCheck} color="#1A5BF6" />
            <Text fontSize="sm" fontWeight="medium">Won: 120</Text>
          </HStack>
          <HStack>
            <Icon as={FaTimes} color="red.500" />
            <Text fontSize="sm" fontWeight="medium">Lost: 51</Text>
          </HStack>
          <HStack>
            <Icon as={FaExclamation} color="orange.500" />
            <Text fontSize="sm" fontWeight="medium">Open: 38</Text>
          </HStack>
        </HStack>
      </Flex>
    );
  }, [analysisView]);
  
  // Win-Loss Analysis by Lead Source
  const WinLossBySourceChart = useCallback(() => {
    const { sources, won, lost } = leadSourceData;
    
    const options = {
      chart: {
        type: 'bar',
        stacked: true,
        toolbar: {
          show: false
        },
        fontFamily: 'inherit',
        redrawOnWindowResize: true,
        animations: {
          enabled: true
        }
      },
      colors: ['#1A5BF6', '#F56565'], // Brand blue for won, red for lost
      plotOptions: {
        bar: {
          horizontal: true,
          borderRadius: 6,
          dataLabels: {
            position: 'center',
          },
          barHeight: '80%',
        },
      },
      dataLabels: {
        enabled: true,
        formatter: function(val) {
          return Math.abs(Math.round(val));
        },
        style: {
          fontSize: '12px',
          colors: ['#fff']
        },
        offsetX: 0,
        dropShadow: {
          enabled: false
        }
      },
      grid: {
        xaxis: {
          lines: {
            show: false
          }
        },
        yaxis: {
          lines: {
            show: false
          }
        }
      },
      xaxis: {
        type: 'category',
        categories: sources,
        labels: {
          formatter: function(val) {
            return Math.abs(Math.round(val));
          },
          style: {
            fontSize: '11px'
          }
        },
        axisBorder: {
          show: false
        },
        axisTicks: {
          show: false
        }
      },
      yaxis: {
        labels: {
          style: {
            fontSize: '11px',
            fontWeight: 500
          },
          maxWidth: 120,
        }
      },
      tooltip: {
        shared: true,
        intersect: false,
      },
      legend: {
        show: false
      },
      responsive: [{
        breakpoint: 480,
        options: {
          yaxis: {
            labels: {
              style: {
                fontSize: '10px'
              },
              maxWidth: 80
            }
          },
          dataLabels: {
            style: {
              fontSize: '10px'
            }
          }
        }
      }]
    };
    
    const series = [
      {
        name: 'Won',
        data: won
      },
      {
        name: 'Lost',
        data: lost
      }
    ];
    
    // Analysis of the lead source data
    const getBestSource = () => {
      let bestIndex = 0;
      let bestRatio = 0;
      
      won.forEach((winCount, index) => {
        const lossCount = lost[index];
        const ratio = winCount / (winCount + lossCount);
        if (ratio > bestRatio) {
          bestRatio = ratio;
          bestIndex = index;
        }
      });
      
      return {
        source: sources[bestIndex],
        winRate: Math.round(bestRatio * 100)
      };
    };
    
    const getWorstSource = () => {
      let worstIndex = 0;
      let worstRatio = 1;
      
      won.forEach((winCount, index) => {
        const lossCount = lost[index];
        const ratio = winCount / (winCount + lossCount);
        if (ratio < worstRatio) {
          worstRatio = ratio;
          worstIndex = index;
        }
      });
      
      return {
        source: sources[worstIndex],
        winRate: Math.round(worstRatio * 100)
      };
    };
    
    const bestSource = getBestSource();
    const worstSource = getWorstSource();
    
    return (
      <Box>
        <Box height={{ base: "250px", md: "280px" }} py={2}>
          <ApexCharts
            options={options}
            series={series}
            type="bar"
            height="100%"
            width="100%"
          />
        </Box>
        
        <Box mt={4} p={{ base: 3, md: 4 }} bg="white" borderRadius="md" borderWidth="1px" borderColor="gray.200">
          <Flex 
            direction={{ base: "column", sm: "row" }} 
            justify="space-between" 
            gap={{ base: 3, sm: 4 }}
          >
            <VStack align="start" spacing={0}>
              <Text fontSize="sm" color="gray.600">Best Performing Source:</Text>
              <HStack>
                <Badge colorScheme="blue" fontSize="sm">{bestSource.source}</Badge>
                <Text fontSize="sm" fontWeight="bold">{bestSource.winRate}% Win Rate</Text>
              </HStack>
            </VStack>
            
            <VStack align="start" spacing={0}>
              <Text fontSize="sm" color="gray.600">Needs Improvement:</Text>
              <HStack>
                <Badge colorScheme="red" fontSize="sm">{worstSource.source}</Badge>
                <Text fontSize="sm" fontWeight="bold">{worstSource.winRate}% Win Rate</Text>
              </HStack>
            </VStack>
          </Flex>
        </Box>
      </Box>
    );
  }, []);
  
  // Win-Loss Analysis by Team Member
  const WinLossByTeamChart = useCallback(() => {
    const { members, won, lost } = teamMemberData;
    
    const options = {
      chart: {
        type: 'bar',
        stacked: true,
        toolbar: {
          show: false
        },
        fontFamily: 'inherit',
        redrawOnWindowResize: true,
        animations: {
          enabled: true
        }
      },
      colors: ['#1A5BF6', '#F56565'], // Brand blue for won, red for lost
      plotOptions: {
        bar: {
          horizontal: true,
          borderRadius: 6,
          dataLabels: {
            position: 'center',
          },
          barHeight: '80%',
        },
      },
      dataLabels: {
        enabled: true,
        formatter: function(val) {
          return Math.abs(Math.round(val));
        },
        style: {
          fontSize: '12px',
          colors: ['#fff']
        },
        offsetX: 0,
        dropShadow: {
          enabled: false
        }
      },
      grid: {
        xaxis: {
          lines: {
            show: false
          }
        },
        yaxis: {
          lines: {
            show: false
          }
        }
      },
      xaxis: {
        type: 'category',
        categories: members.map(name => {
          // For mobile, display only first name if needed
          return window.innerWidth < 480 ? name.split(' ')[0] : name;
        }),
        labels: {
          formatter: function(val) {
            return Math.abs(Math.round(val));
          },
          style: {
            fontSize: '11px'
          }
        },
        axisBorder: {
          show: false
        },
        axisTicks: {
          show: false
        }
      },
      yaxis: {
        labels: {
          style: {
            fontSize: '11px',
            fontWeight: 500
          },
          maxWidth: 120,
          formatter: function(val) {
            // For mobile, truncate long names
            if (window.innerWidth < 480) {
              const name = String(val);
              return name.length > 12 ? name.slice(0, 10) + '...' : name;
            }
            return val;
          }
        }
      },
      tooltip: {
        shared: true,
        intersect: false,
      },
      legend: {
        show: false
      },
      responsive: [{
        breakpoint: 480,
        options: {
          yaxis: {
            labels: {
              style: {
                fontSize: '10px'
              },
              maxWidth: 80
            }
          },
          dataLabels: {
            style: {
              fontSize: '10px'
            }
          }
        }
      }]
    };
    
    const series = [
      {
        name: 'Won',
        data: won
      },
      {
        name: 'Lost',
        data: lost
      }
    ];
    
    // Analysis of the team member data
    const getTopPerformer = () => {
      let bestIndex = 0;
      let bestRatio = 0;
      
      won.forEach((winCount, index) => {
        const lossCount = lost[index];
        const ratio = winCount / (winCount + lossCount);
        if (ratio > bestRatio) {
          bestRatio = ratio;
          bestIndex = index;
        }
      });
      
      return {
        member: members[bestIndex],
        winRate: Math.round(bestRatio * 100),
        deals: won[bestIndex]
      };
    };
    
    const getMostDeals = () => {
      let bestIndex = 0;
      let mostDeals = 0;
      
      won.forEach((winCount, index) => {
        if (winCount > mostDeals) {
          mostDeals = winCount;
          bestIndex = index;
        }
      });
      
      return {
        member: members[bestIndex],
        deals: mostDeals
      };
    };
    
    const topPerformer = getTopPerformer();
    const mostDeals = getMostDeals();
    
    return (
      <Box>
        <Box height={{ base: "250px", md: "280px" }} py={2}>
          <ApexCharts
            options={options}
            series={series}
            type="bar"
            height="100%"
            width="100%"
          />
        </Box>
        
        <Box mt={4} p={{ base: 3, md: 4 }} bg="white" borderRadius="md" borderWidth="1px" borderColor="gray.200">
          <Flex 
            direction={{ base: "column", sm: "row" }} 
            justify="space-between" 
            gap={{ base: 3, sm: 4 }}
          >
            <VStack align="start" spacing={0}>
              <Text fontSize="sm" color="gray.600">Highest Win Rate:</Text>
              <HStack>
                <Badge colorScheme="blue" fontSize="sm">{topPerformer.member.split(' ')[0]}</Badge>
                <Text fontSize="sm" fontWeight="bold">{topPerformer.winRate}% Win Rate</Text>
              </HStack>
            </VStack>
            
            <VStack align="start" spacing={0}>
              <Text fontSize="sm" color="gray.600">Most Deals Won:</Text>
              <HStack>
                <Badge colorScheme="green" fontSize="sm">{mostDeals.member.split(' ')[0]}</Badge>
                <Text fontSize="sm" fontWeight="bold">{mostDeals.deals} Deals</Text>
              </HStack>
            </VStack>
          </Flex>
        </Box>
      </Box>
    );
  }, []);
  
  // Win-Loss Analysis Content
  const WinLossAnalysisContent = useCallback(() => {
    return (
      <Box>
        <WinLossToggle />
        {analysisView === "source" ? <WinLossBySourceChart /> : <WinLossByTeamChart />}
      </Box>
    );
  }, [analysisView, WinLossToggle, WinLossBySourceChart, WinLossByTeamChart]);

  return (
    <DashboardSection
      title="Win-Loss Analysis"
      description="Understand your wins and losses. Identify patterns in successful deals, recognize warning signs early, and refine your strategy to improve win rates."
      icon={FaChartPie}
      iconColor={iconColor}
      bgColor={bgColor}
      isReversed={false}
      chartComponent={<WinLossAnalysisContent />}
      showButton={false}
    />
  );
});

// Add display name
WinLossSection.displayName = "WinLossSection";

const LandingReportsSection = () => {
  const bgColor = useColorModeValue("white", "gray.900");
  const altBgColor = useColorModeValue("gray.50", "gray.800");
  const textColor = useColorModeValue("gray.800", "white");
  const secondaryTextColor = useColorModeValue("gray.600", "gray.400");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  
  // Add state and ref for visibility detection
  const [isVisible, setIsVisible] = useState(false);
  const ctaRef = useRef(null);

  // Modal disclosure
  const { isOpen, onOpen, onClose } = useDisclosure();
  
  // Set up intersection observer
  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1
    };
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        setIsVisible(entry.isIntersecting);
      });
    }, options);
    
    if (ctaRef.current) {
      observer.observe(ctaRef.current);
    }
    
    return () => {
      if (ctaRef.current) {
        observer.unobserve(ctaRef.current);
      }
    };
  }, []);

  return (
    <Box>
      {/* Headline Section */}
      <Box bg={bgColor} py={{ base: 12, md: 14 }}>
        <Container maxW="1400px" px={{ base: 4, md: 8 }}>
          <VStack spacing={{ base: 2, md: 4 }} align="center" textAlign="center">
            <Flex
              bg="purple.50"
              borderRadius="full"
              p={2}
              alignItems="center"
              justifyContent="center"
              boxSize={{ base: "50px", md: "60px" }}
              mb={2}
            >
              <Icon as={MdEqualizer} color="purple.500" boxSize={{ base: 6, md: 8 }} />
            </Flex>
            
            <Heading
              as="h2"
              fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
              fontWeight="bold"
              color={textColor}
              mb={1}
            >
              Smarter Sales Insights
            </Heading>
            
            <Text
              fontSize={{ base: "md", md: "lg" }}
              maxW="800px"
              color={secondaryTextColor}
            >
              Intuitive, powerful, and actionable, Lysto&apos;s reports help you unlock the full potential of your sales data.
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* Each section is now an independent component with its own state */}
      <PerformanceSection bgColor={bgColor} iconColor="orange" />
      <TargetSalesSection bgColor={bgColor} />
      <WinLossSection bgColor={bgColor} />
          
      {/* Call to Action - Redesigned to match LandingMeetingSection */}
      <Box bg={bgColor} py={{ base: 8, md: 10 }}>
        <Container maxW="1400px" px={{ base: 4, md: 8 }}>
          <Box 
            ref={ctaRef}
            p={{ base: 6, md: 8 }} 
            bg={altBgColor} 
            borderRadius="xl" 
            borderWidth="1px" 
            borderColor={borderColor}
            boxShadow="lg"
            as={motion.div}
            // Only apply hover animation when visible
            whileHover={isVisible ? { y: -5 } : {}}
            transition={{ duration: 0.3 }}
          >
            <Grid templateColumns={{ base: "1fr", md: "2fr 1fr" }} gap={{ base: 4, md: 6 }} alignItems="center">
              <GridItem>
                <Heading as="h3" size="lg" mb={2}>
                  Join the future of sales, today.
                </Heading>
                <Text color={secondaryTextColor} mb={3} fontSize="lg">
                  Transform your revenue team in less time than it took you to read this site.
                </Text>
                
                <HStack spacing={{ base: 4, md: 6 }} mt={4} flexWrap="wrap">
                  <HStack>
                    <Icon as={FaChartLine} color="blue.500" boxSize={5} />
                    <Text fontWeight="medium">Real-time insights</Text>
                  </HStack>
                  <HStack>
                    <Icon as={FaRobot} color="blue.500" boxSize={5} />
                    <Text fontWeight="medium">AI-powered analytics</Text>
                  </HStack>
                  <HStack>
                    <Icon as={FaRocket} color="blue.500" boxSize={5} />
                    <Text fontWeight="medium">Accelerate growth</Text>
                  </HStack>
                </HStack>
              </GridItem>
              
              <GridItem>
                <Flex justifyContent="center">
                  <Button
                    as={motion.button}
                    whileHover={isVisible ? { scale: 1.05 } : {}}
                    whileTap={isVisible ? { scale: 0.95 } : {}}
                    transition={{ duration: 0.2 }}
                    size="lg"
                    leftIcon={<Icon as={MdRocketLaunch} />}
                    colorScheme="blue"
                    px={8}
                    height="60px"
                    fontSize="lg"
                    borderRadius="md"
                    boxShadow="md"
                    onClick={onOpen}
                  >
                    Sign Up
                  </Button>
                </Flex>
              </GridItem>
            </Grid>
          </Box>
        </Container>
      </Box>

      {/* Waitlist Modal */}
      <WaitlistModal isOpen={isOpen} onClose={onClose} />
    </Box>
  );
};

export default LandingReportsSection; 